import { useState, useRef, useEffect } from "react";
import { ModalOverlay } from "@components/atoms";
import {
  LoginStep,
  AccountCreationStep,
  SuccessStep,
} from "@components/molecules";
import { useAuth } from "@app/providers/useAuth";
import { getValidationError, logger } from "@shared/utils";

type ModalStep = "login" | "signup" | "account-creation" | "success";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Map Firebase auth error codes to user-friendly Spanish messages
 */
function mapAuthError(error: string): string {
  if (
    error.includes("invalid-credential") ||
    error.includes("wrong-password") ||
    error.includes("user-not-found")
  ) {
    return "Correo o contraseña incorrectos";
  }

  // ✅ NUEVO: email no verificado
  if (
    error.includes("email-not-verified") ||
    error.toLowerCase().includes("verificar")
  ) {
    return "Debes verificar tu correo antes de iniciar sesión";
  }

  if (error.includes("too-many-requests")) {
    return "Demasiados intentos fallidos. Intenta más tarde";
  }

  if (error.includes("email-already-in-use")) {
    return "Este correo ya está registrado";
  }

  if (error.includes("network-request-failed")) {
    return "Sin conexión. Verifica tu internet";
  }

  if (error.includes("unauthorized-domain")) {
    return "Dominio no autorizado en Firebase. Agrega localhost en Firebase Console → Authentication → Settings → Authorized domains";
  }

  if (error.includes("popup-blocked")) {
    return "El popup fue bloqueado por el navegador. Permite popups para este sitio e intenta nuevamente";
  }

  if (error.includes("popup-closed-by-user")) {
    return "Se cerró el popup antes de completar el inicio de sesión";
  }

  if (error.includes("cancelled-popup-request")) {
    return "Ya hay un popup de Google abierto";
  }

  if (error.includes("redirect-initiated")) {
    // Page is navigating to Google — no error to show
    return "";
  }

  // Show the raw Firebase error code in dev to help diagnose unknown errors
  return `Error de autenticación: ${error}`;
}
export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [step, setStep] = useState<ModalStep>("login");

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Login / Signup fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Account creation fields
  const [fullName, setFullName] = useState("");
  const [cedula, setCedula] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNotifications, setAcceptNotifications] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [authError, setAuthError] = useState("");
  const [isLoading, setIsLoading] = useState(false); // ← add this
  const [isGoogleLoading, setIsGoogleLoading] = useState(false); // ← add this

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFullName("");
    setCedula("");
    setPhone("");
    setAddress("");
    setAcceptTerms(false);
    setAcceptNotifications(false);
    setErrors({});
    setAuthError(""); // 🔥 FALTA ESTO
    setIsLoading(false); // ← reset loading on close
    setStep("login");
    setIsGoogleLoading(false); // ← add this
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Validation checks for login step
  const isLoginValid =
    email.trim() !== "" &&
    password.trim() !== "" &&
    !getValidationError("email", email) &&
    !getValidationError("password", password);

  // Validation checks for signup step
  const isSignupValid =
    email.trim() !== "" &&
    password.trim() !== "" &&
    confirmPassword.trim() !== "" &&
    !getValidationError("email", email) &&
    !getValidationError("password", password) &&
    !getValidationError("confirmPassword", confirmPassword, password);

  // Validation checks for account creation step
  const isCreationValid =
    fullName.trim() !== "" &&
    cedula.trim() !== "" &&
    phone.trim() !== "" &&
    address.trim() !== "" &&
    acceptTerms &&
    !getValidationError("fullName", fullName) &&
    !getValidationError("cedula", cedula) &&
    !getValidationError("phone", phone) &&
    !getValidationError("address", address);

  // AFTER
  const handleContinue = async () => {
    setAuthError("");
    setIsLoading(true); // ← start loading
    try {
      if (step === "login") {
        const error = await signIn(email, password);
        if (error) {
          setAuthError(mapAuthError(error));
          return;
        }
        handleClose();
      } else if (step === "signup") {
        setStep("account-creation");
      } else if (step === "account-creation") {
        const error = await signUp(email, password, {
          fullName,
          cedula,
          phone,
          address,
        });
        if (error) {
          setAuthError(mapAuthError(error));
          return;
        }
        setStep("success");
      }
    } catch (error) {
      logger.error("Auth error:", error);
    } finally {
      setIsLoading(false); // ← always stop loading
    }
  };

  const handleAcceptSuccess = () => {
    handleClose();
  };

  const handleToggleAuthStep = () => {
    setErrors({});
    setAuthError("");
    setStep(step === "login" ? "signup" : "login");
  };

  // AFTER
  const handleGoogleLogin = async () => {
    if (!signInWithGoogle) return;
    setIsGoogleLoading(true);
    setAuthError("");
    try {
      const error = await signInWithGoogle();
      if (!isMountedRef.current) return;
      if (error) {
        setAuthError(mapAuthError(error));
        return;
      }
      handleClose();
    } finally {
      if (isMountedRef.current) setIsGoogleLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setAuthError("");
  };

  const handleEmailBlur = () => {
    const error = getValidationError("email", email);
    setErrors((prev) => ({
      ...prev,
      email: error || undefined,
    }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setAuthError("");
  };

  const handlePasswordBlur = () => {
    const error = getValidationError("password", password);
    setErrors((prev) => ({
      ...prev,
      password: error || undefined,
    }));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
  };

  const handleConfirmPasswordBlur = () => {
    const error = getValidationError(
      "confirmPassword",
      confirmPassword,
      password,
    );
    setErrors((prev) => ({
      ...prev,
      confirmPassword: error || undefined,
    }));
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
  };

  const handleFullNameBlur = () => {
    const error = getValidationError("fullName", fullName);
    setErrors((prev) => ({
      ...prev,
      fullName: error || undefined,
    }));
  };

  const handleCedulaChange = (value: string) => {
    setCedula(value);
  };

  const handleCedulaBlur = () => {
    const error = getValidationError("cedula", cedula);
    setErrors((prev) => ({
      ...prev,
      cedula: error || undefined,
    }));
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
  };

  const handlePhoneBlur = () => {
    const error = getValidationError("phone", phone);
    setErrors((prev) => ({
      ...prev,
      phone: error || undefined,
    }));
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
  };

  const handleAddressBlur = () => {
    const error = getValidationError("address", address);
    setErrors((prev) => ({
      ...prev,
      address: error || undefined,
    }));
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
      <div
        className={`flex w-full flex-col rounded-2xl bg-white max-h-[90vh] overflow-hidden ${step === "account-creation" ? "max-w-3xl" : "max-w-[380px]"}`}
      >
        <div className="flex flex-col overflow-y-auto px-6 py-8 md:px-5">
          {/* Success Step */}
          {step === "success" && (
            <SuccessStep onAccept={handleAcceptSuccess} email={email} />
          )}

          {/* Login / Signup Step */}

          {(step === "login" || step === "signup") && (
            <LoginStep
              email={email}
              password={password}
              confirmPassword={confirmPassword}
              step={step}
              onEmailChange={handleEmailChange}
              onPasswordChange={handlePasswordChange}
              onConfirmPasswordChange={handleConfirmPasswordChange}
              onEmailBlur={handleEmailBlur}
              onPasswordBlur={handlePasswordBlur}
              onConfirmPasswordBlur={handleConfirmPasswordBlur}
              onToggleStep={handleToggleAuthStep}
              onClose={handleClose}
              isValid={step === "login" ? isLoginValid : isSignupValid}
              onContinue={handleContinue}
              onGoogleLogin={signInWithGoogle ? handleGoogleLogin : undefined}
              emailError={errors.email}
              passwordError={errors.password}
              confirmPasswordError={errors.confirmPassword}
              authError={authError}
              isGoogleLoading={isGoogleLoading} // ← add this
            />
          )}

          {/* Account Creation Step */}
          {step === "account-creation" && (
            <AccountCreationStep
              fullName={fullName}
              cedula={cedula}
              phone={phone}
              address={address}
              acceptTerms={acceptTerms}
              acceptNotifications={acceptNotifications}
              onFullNameChange={handleFullNameChange}
              onCedulaChange={handleCedulaChange}
              onPhoneChange={handlePhoneChange}
              onAddressChange={handleAddressChange}
              onFullNameBlur={handleFullNameBlur}
              onCedulaBlur={handleCedulaBlur}
              onPhoneBlur={handlePhoneBlur}
              onAddressBlur={handleAddressBlur}
              onAcceptTermsChange={setAcceptTerms}
              onAcceptNotificationsChange={setAcceptNotifications}
              onClose={handleClose}
              isValid={isCreationValid}
              isLoading={isLoading} // ← pass it down
              onContinue={handleContinue}
              fullNameError={errors.fullName}
              cedulaError={errors.cedula}
              phoneError={errors.phone}
              addressError={errors.address}
            />
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
